import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTourContext } from '../../contexts/TourContext';

// Workaround for framer-motion type issue with React 18
const SafeAnimatePresence = AnimatePresence as any;

const TourTooltip: React.FC = () => {
    const { t } = useTranslation();
    const {
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps,
        nextStep,
        previousStep,
        skipTour,
        completeTour  // Adicionado para finalização do tour
    } = useTourContext();

    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [isVisible, setIsVisible] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isActive || !currentStep) {
            setIsVisible(false);
            return;
        }

        // Função para verificar se elemento existe e está visível
        const checkElement = () => {
            const targetElement = document.querySelector(currentStep.targetSelector);

            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();
                const tooltipHeight = tooltipRef.current?.offsetHeight || 0;
                const tooltipWidth = tooltipRef.current?.offsetWidth || 0;

                let top = 0;
                let left = 0;

                // Calcula posição baseado na configuração do step
                switch (currentStep.position) {
                    case 'top':
                        top = rect.top - tooltipHeight - 20;
                        left = rect.left + rect.width / 2 - tooltipWidth / 2;
                        break;
                    case 'bottom':
                        top = rect.bottom + 20;
                        left = rect.left + rect.width / 2 - tooltipWidth / 2;
                        break;
                    case 'left':
                        top = rect.top + rect.height / 2 - tooltipHeight / 2;
                        left = rect.left - tooltipWidth - 20;
                        break;
                    case 'right':
                        top = rect.top + rect.height / 2 - tooltipHeight / 2;
                        left = rect.right + 20;
                        break;
                    case 'center':
                        top = window.innerHeight / 2 - tooltipHeight / 2;
                        left = window.innerWidth / 2 - tooltipWidth / 2;
                        break;
                }

                // Ajuste para mobile - sempre posiciona embaixo em telas pequenas
                if (window.innerWidth < 768 && currentStep.position !== 'center') {
                    top = window.innerHeight - tooltipHeight - 80;
                    left = 16;
                }

                // Garante que tooltip fica dentro da viewport
                top = Math.max(16, Math.min(top, window.innerHeight - tooltipHeight - 16));
                left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));

                setTooltipPosition({ top, left });
                setIsVisible(true);
            }
        };

        // Se waitForSelector está ativado, aguarda elemento aparecer com retry
        if (currentStep.waitForSelector) {
            let attempts = 0;
            const maxAttempts = 20; // Até 2000ms total (20 x 100ms) - tempo para navegação

            const waitForElement = () => {
                const element = document.querySelector(currentStep.targetSelector);

                if (element && element.getBoundingClientRect().height > 0) {
                    // Elemento existe E está visível - calcula posição
                    checkElement();
                } else if (attempts < maxAttempts) {
                    // Tenta novamente
                    attempts++;
                    setTimeout(waitForElement, 100); // 100ms entre tentativas (mais confiável)
                } else {
                    // Timeout - mostra mesmo assim
                    console.warn(`[Tour] Elemento não encontrado após ${maxAttempts * 100}ms:`, currentStep.targetSelector);
                    checkElement();
                }
            };

            waitForElement();
        } else {
            // Comportamento original - checa imediatamente com retry básico
            checkElement();
            const timeoutId = setTimeout(checkElement, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [isActive, currentStep]);

    if (!isActive || !currentStep || !isVisible) return null;

    const isFirstStep = currentStepIndex === 0;
    // Detecta se é o último passo do tour
    const isLastStep = currentStepIndex === totalSteps - 1;

    // Handler inteligente: completa tour no último passo, avança nos outros
    const handleNextAction = () => {
        if (isLastStep) {
            completeTour();
        } else {
            nextStep();
        }
    };

    return (
        <SafeAnimatePresence>
            <motion.div
                ref={tooltipRef}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 25 }}
                className="fixed z-[9999]"
                style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    maxWidth: window.innerWidth < 768 ? 'calc(100vw - 32px)' : '400px',
                }}
            >
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 relative">
                    {/* Progress indicator */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex gap-1.5">
                            {Array.from({ length: totalSteps }).map((_, index) => (
                                <div
                                    key={index}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${index === currentStepIndex
                                        ? 'w-8 bg-primary dark:bg-primary-darkMode'
                                        : index < currentStepIndex
                                            ? 'w-1.5 bg-primary/50 dark:bg-primary-darkMode/50'
                                            : 'w-1.5 bg-gray-300 dark:bg-gray-600'
                                        }`}
                                />
                            ))}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {currentStepIndex + 1} / {totalSteps}
                        </span>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {t(currentStep.title)}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                        {t(currentStep.description)}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={skipTour}
                            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium transition-colors"
                        >
                            {t('tour.skip')}
                        </button>

                        <div className="flex gap-2">
                            {!isFirstStep && (
                                <motion.button
                                    onClick={previousStep}
                                    className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {t('tour.previous')}
                                </motion.button>
                            )}
                            {/* Changed next button to use smart handler */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleNextAction}
                                disabled={currentStep?.requireAction && currentStep?.blockNext}
                                className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all ${currentStep?.requireAction && currentStep?.blockNext
                                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                    : 'bg-primary dark:bg-primary-darkMode text-white hover:shadow-lg'
                                    }`}
                            >
                                {isLastStep ? t('tour.finish') : t('tour.next')}
                            </motion.button>
                        </div>
                    </div>

                    {/* Arrow indicator (only on desktop and not center) */}
                    {window.innerWidth >= 768 && currentStep.position !== 'center' && (
                        <div
                            className={`absolute w-0 h-0 ${currentStep.position === 'top'
                                ? 'bottom-[-8px] left-1/2 -translate-x-1/2 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white dark:border-t-gray-800'
                                : currentStep.position === 'bottom'
                                    ? 'top-[-8px] left-1/2 -translate-x-1/2 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-white dark:border-b-gray-800'
                                    : currentStep.position === 'left'
                                        ? 'right-[-8px] top-1/2 -translate-y-1/2 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-white dark:border-l-gray-800'
                                        : 'left-[-8px] top-1/2 -translate-y-1/2 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-white dark:border-r-gray-800'
                                }`}
                        />
                    )}
                </div>
            </motion.div>
        </SafeAnimatePresence>
    );
};

export default TourTooltip;
